import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
} from '@mui/material';
import { RuleEditor } from './RuleEditor';

export type Rule = {
  type: 'airtime' | 'data';
  trigger: {
    threshold: number;
  };
  action: {
    topup?: string; // Product ID for data
    amount?: number; // Amount for airtime
  };
};

type AutoTopUpConfigModalProps = {
  open: boolean;
  onClose: (updatedConfig?: { enabled: boolean; rules: Rule[] } | null) => void;
  simId: string;
  initialConfig?: Rule[];
  initialEnabled: boolean;
  isClusterLevel?: boolean;
};

export function AutoTopUpConfigModal({
  open,
  onClose,
  simId,
  initialConfig = [],
  initialEnabled,
  isClusterLevel,
}: AutoTopUpConfigModalProps) {
  const [enabled, setEnabled] = useState(initialEnabled);
  const [rules, setRules] = useState<Rule[]>(initialConfig ?? []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setEnabled(initialEnabled);
    setRules(initialConfig ?? []);
    setError(null);
  }, [initialEnabled, initialConfig, open]);

  const handleSave = async () => {
    setError(null);
    setLoading(true);
    try {
      onClose({ enabled, rules: enabled ? rules : [] });
    } catch (err: any) {
      console.error('Error during modal save callback:', err);
      setError('An unexpected error occurred while closing.');
      onClose(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = (event: {}, reason: "backdropClick" | "escapeKeyDown") => {
    onClose(null);
  };

  const modalTitle = isClusterLevel 
    ? 'Configure Cluster Default Auto Top-Up Rules' 
    : 'Configure SIM Auto Top-Up Rules';

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth>
      <DialogTitle>{modalTitle}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
            }
            label="Enable Auto Top-Up"
          />
        </Box>

        {enabled && (
          <RuleEditor rules={rules} onRulesChange={setRules} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(null)}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
} 